import { useCallback, useState, useEffect } from "react";

import {
  getLessonEventsClient,
  type HintUsageResponse,
  type CtfSubmissionResponse,
  type LessonInstructions,
} from "sj-ui-toolkit/src/client";

/**
 * Records step completions, hint usage, and CTF submissions to the lesson events service.
 */

export interface LessonInteractionResult {
  maxStepCompleted: number | null;
  allStepsCompleted: boolean;
  correctFlagSubmitted: boolean;
  reportStepsCompleted: (numStepsCompleted: number) => Promise<boolean>;
  reportAllStepsCompleted: () => Promise<void>;
  useHint: (hintId: string) => Promise<HintUsageResponse | null>;
  submitCtfProof: (proof: string) => Promise<CtfSubmissionResponse | null>;
  clientError: Error | null;
}

export const useLessonInteractionInternal = (
  tenant: string,
  lessonKey: string,
  instructions: LessonInstructions,
  lessonLoadedAt: Date | null,
  tournamentUuid?: string
): LessonInteractionResult => {
  const eventsClient = getLessonEventsClient();
  const [clientError, setClientError] = useState<Error | null>(null);
  const [maxStepCompleted, setMaxStepCompleted] = useState<number | null>(null);
  const [allStepsCompleted, setAllStepsCompleted] = useState<boolean>(false);
  const [correctFlagSubmitted, setCorrectFlagSubmitted] = useState<boolean>(false);

  // Report the number of lesson steps completed by the user.
  // Never reports the last step - must call reportAllStepsCompleted separately.
  const reportStepsCompleted = useCallback(
    async (completedStep: number) => {
      // We can't record steps if we don't know when the lesson was loaded
      if (!lessonLoadedAt) {
        return false;
      }

      if (
        completedStep > 0 &&
        completedStep < instructions.steps.length &&
        (maxStepCompleted === null || completedStep > maxStepCompleted)
      ) {
        setMaxStepCompleted(completedStep);

        await eventsClient
          .recordStepsCompleted(lessonKey, lessonLoadedAt, completedStep)
          .promise.catch(err => {
            setClientError(err);
          });
        return true;
      }

      return false;
    },
    [maxStepCompleted, lessonKey, lessonLoadedAt, instructions.steps.length]
  );

  const reportAllStepsCompleted = useCallback(async () => {
    // We can't record steps if we don't know when the lesson was loaded
    if (!lessonLoadedAt) {
      return;
    }

    await eventsClient
      .recordStepsCompleted(lessonKey, lessonLoadedAt, instructions.steps.length)
      .promise.catch(err => {
        setClientError(err);
      });

    setAllStepsCompleted(true);
  }, [lessonKey, instructions.steps.length, lessonLoadedAt]);

  // Record user's usage of a hint by its ID
  const useHint = useCallback(
    async (hintId: string): Promise<HintUsageResponse | null> => {
      const foundStep = instructions.steps.find(step =>
        step.hints.find(hint => hint.id === hintId)
      );

      if (!foundStep || !lessonLoadedAt) {
        return null;
      }

      return await eventsClient
        .recordHintUsage(lessonKey, lessonLoadedAt, hintId)
        .promise.catch(err => {
          setClientError(err);
          return null;
        });
    },
    [lessonKey, lessonLoadedAt, instructions.steps]
  );

  // Submit a CTF proof for the lesson
  const submitCtfProof = useCallback(
    async (proof: string): Promise<CtfSubmissionResponse | null> => {
      if (!instructions.requiresFlagSubmission || !lessonLoadedAt) {
        return null;
      }

      const response = await eventsClient
        .recordCtfSubmission(lessonKey, lessonLoadedAt, proof)
        .promise.catch(err => {
          setClientError(err);
          return null;
        });

      if (response && response.accepted) {
        setCorrectFlagSubmitted(true);
      }
      return response;
    },
    [lessonKey, lessonLoadedAt, instructions.requiresFlagSubmission]
  );

  // Once the lesson is loaded, set an context for future interactions
  useEffect(() => {
    const initInteractionContext = async () => {
      if (!lessonLoadedAt) {
        return;
      }

      eventsClient.setEventContext({
        tournamentUuid,
        tenant,
      });
    };

    initInteractionContext();
  }, [lessonLoadedAt, tenant, tournamentUuid]);

  return {
    maxStepCompleted,
    allStepsCompleted,
    correctFlagSubmitted,
    reportStepsCompleted,
    reportAllStepsCompleted,
    useHint,
    submitCtfProof,
    clientError,
  };
};
